import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  AddBrandProduct,
  UpdateBrandProduct,
} from "../../../API/endpoints/Brands";
import { useEffect } from "react";

interface AddEditProductModalProps {
  isModalOpen: string;
  handleCloseModal: () => void;
  multiBrand?: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  brandList: {
    name: string;
    value: string;
    categories: { focus_category: string; sub_categories: string[] }[];
  }[];
  defaultBrand?: string;
  selectedProduct: any | null;
}

interface AddEditProductPayloadDTO {
  name: string;
  brand_id: string;
  category: string;
  sub_category: string;
  description?: string;
  product_link?: string;
}

function AddEditProductModal(props: AddEditProductModalProps) {
  const { register, formState, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      name: "",
      brand_id: "",
      category: "",
      sub_category: "",
      description: "",
      product_link: "",
    },
  });

  useEffect(() => {
    if (props.isModalOpen === "Add") {
      reset({
        name: "",
        brand_id: props.defaultBrand || "",
        category: "",
        sub_category: "",
        description: "",
        product_link: "",
      });
    } else if (props.isModalOpen === "Edit") {
      if (props.selectedProduct) {
        reset({
          name: props.selectedProduct.name,
          brand_id: props.selectedProduct.brand_id,
          category: props.selectedProduct.category,
          sub_category: props.selectedProduct.sub_category,
          description: props.selectedProduct?.description || "",
          product_link: props.selectedProduct?.product_link || "",
        });
      }
    }
  }, [props.isModalOpen, reset]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "brand_id") {
        reset({
          ...value,
          category: "",
          sub_category: "",
        });
      } else if (name === "category") {
        reset({
          ...value,
          sub_category: "",
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, reset]);

  const handleSubmitForm = async (data: any) => {
    const payload: AddEditProductPayloadDTO = {
      name: data.name,
      brand_id: data.brand_id,
      category: data.category,
      sub_category: data.sub_category,
    };
    if (data.description) {
      payload.description = data.description;
    }
    if (data.product_link) {
      payload.product_link = data.product_link;
    }
    if (props.isModalOpen === "Add") {
      await AddBrandProduct(payload);
    } else {
      await UpdateBrandProduct(props.selectedProduct.id, payload);
    }
    props.setRefresh((prev) => !prev);
    reset();
    props.handleCloseModal();
  };

  const { errors } = formState;

  // Get the categories for the selected brand from brandList
  const selectedBrandId = watch("brand_id");
  const selectedBrandObj = props.brandList.find(
    (b) => b.value === selectedBrandId
  );
  const brandCategories = selectedBrandObj?.categories || [];

  const selectedCategory = watch("category");
  const selectedCategoryObj = brandCategories.find(
    (cat) => cat.focus_category === selectedCategory
  );
  const subCategories = selectedCategoryObj?.sub_categories || [];

  return (
    <Modal
      show={Boolean(props.isModalOpen)}
      onHide={props.handleCloseModal}
      size="lg"
    >
      <Modal.Header closeButton>
        {props.isModalOpen === "Add" ? (
          <Modal.Title className="font16-medium gray-800">
            Add New Product
          </Modal.Title>
        ) : (
          <Modal.Title className="font16-medium gray-800">Edit</Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={handleSubmit((data) => {
            handleSubmitForm(data);
          })}
        >
          <Row>
            <Col>
              <Form.Group className="mb-3 formgroup" controlId="name">
                <Form.Label>Product Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Product Name"
                  {...register("name", {
                    required: "* This field is required",
                  })}
                  name="name"
                />
                <Form.Text muted>
                  <span>
                    {errors?.name && (
                      <p className="error-form">
                        {String(errors.name?.message)}
                      </p>
                    )}
                  </span>
                </Form.Text>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3 formgroup" controlId="brand_id">
                <Form.Label>Brand Name</Form.Label>
                <Form.Select
                  aria-label="20"
                  {...register("brand_id", {
                    required: "* This field is required",
                  })}
                  name="brand_id"
                  disabled={props.multiBrand ? false : true}
                >
                  <option value="">Select Brand</option>
                  {props.brandList.map((brand) => (
                    <option key={brand.value} value={brand.value}>
                      {brand.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  <span>
                    {errors?.brand_id && (
                      <p className="error-form">
                        {String(errors.brand_id?.message)}
                      </p>
                    )}
                  </span>
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3 formgroup" controlId="category">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  aria-label="20"
                  {...register("category", {
                    required: "* This field is required",
                  })}
                  name="category"
                  disabled={!watch("brand_id")}
                >
                  <option value="">Select category</option>
                  {brandCategories.map((cat) => (
                    <option key={cat.focus_category} value={cat.focus_category}>
                      {cat.focus_category}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  <span>
                    {errors?.category && (
                      <p className="error-form">
                        {String(errors.category?.message)}
                      </p>
                    )}
                  </span>
                </Form.Text>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3 formgroup" controlId="sub_category">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  aria-label="20"
                  {...register("sub_category", {
                    required: "* This field is required",
                  })}
                  name="sub_category"
                  disabled={!watch("category")}
                >
                  <option value="">Select sub category</option>
                  {subCategories.map((sub: string) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  <span>
                    {errors?.sub_category && (
                      <p className="error-form">
                        {String(errors.sub_category?.message)}
                      </p>
                    )}
                  </span>
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Form.Group className="mb-3 formgroup" controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                className="h-auto"
                as="textarea"
                rows={3}
                placeholder="Enter a description"
                {...register("description")}
                name="description"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="mb-3 formgroup" controlId="product_link">
              <Form.Label>Product Link</Form.Label>
              <Form.Control
                type="text"
                placeholder="Product Link"
                {...register("product_link", {
                  pattern: {
                    value: /^(https?:\/\/(?:www\.)?[^\s/$.?#].[^\s]*)$/i,
                    message: "Enter a valid URL",
                  },
                })}
                name="product_link"
              />
              <Form.Text muted>
                <span>
                  {errors?.product_link && (
                    <p className="error-form">
                      {String(errors.product_link?.message)}
                    </p>
                  )}
                </span>
              </Form.Text>
            </Form.Group>
          </Row>
          <div className="footer d-flex justify-content-end gap-2 mt-3">
            <Button
              className="btn-secondary12"
              variant="secondary"
              onClick={props.handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              className="btn-primary12 bg-[#0152FB]"
              variant="primary"
              type="submit"
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AddEditProductModal;
